import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  items: [],
  isOpen: false,
  bookingType: 'enquiry', // 'enquiry' or 'payment'
  eventDetails: {
    date: null,
    time: null,
    venue: '',
    occasion: '',
    guestCount: 0,
    dietaryPreferences: {
      vegetarian: 0,
      nonVegetarian: 0,
      vegan: 0,
      specialDietary: []
    },
    specialInstructions: ''
  },
  serviceRequirements: {
    setup: false,
    serving: false,
    cleanup: false,
    staffCount: 0,
    equipment: []
  },
  total: 0,
  itemCount: 0,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const { item, quantity = 1, customizations = {} } = action.payload;
      const existingItem = state.items.find(
        cartItem => cartItem.id === item.id && 
        JSON.stringify(cartItem.customizations) === JSON.stringify(customizations)
      );

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        state.items.push({
          ...item,
          quantity,
          customizations,
          addedAt: new Date().toISOString(),
        });
      }
      
      state.itemCount = state.items.reduce((total, item) => total + item.quantity, 0);
      state.total = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    removeFromCart: (state, action) => {
      const { itemId, customizations } = action.payload;
      state.items = state.items.filter(
        item => !(item.id === itemId && 
        JSON.stringify(item.customizations) === JSON.stringify(customizations))
      );
      
      state.itemCount = state.items.reduce((total, item) => total + item.quantity, 0);
      state.total = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    updateQuantity: (state, action) => {
      const { itemId, quantity, customizations } = action.payload;
      const item = state.items.find(
        cartItem => cartItem.id === itemId && 
        JSON.stringify(cartItem.customizations) === JSON.stringify(customizations)
      );
      
      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter(cartItem => cartItem !== item);
        } else {
          item.quantity = quantity;
        }
      }
      
      state.itemCount = state.items.reduce((total, item) => total + item.quantity, 0);
      state.total = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    clearCart: (state) => {
      state.items = [];
      state.itemCount = 0;
      state.total = 0;
    },
    setCartOpen: (state, action) => {
      state.isOpen = action.payload;
    },
    setBookingType: (state, action) => {
      state.bookingType = action.payload;
    },
    updateEventDetails: (state, action) => {
      state.eventDetails = { ...state.eventDetails, ...action.payload };
    },
    updateServiceRequirements: (state, action) => {
      state.serviceRequirements = { ...state.serviceRequirements, ...action.payload };
    },
    loadCartFromStorage: (state, action) => {
      const { items, eventDetails, serviceRequirements, bookingType } = action.payload;
      state.items = items || [];
      state.eventDetails = { ...state.eventDetails, ...eventDetails };
      state.serviceRequirements = { ...state.serviceRequirements, ...serviceRequirements };
      state.bookingType = bookingType || 'enquiry';
      
      state.itemCount = state.items.reduce((total, item) => total + item.quantity, 0);
      state.total = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    saveCartToStorage: (state) => {
      // This action doesn't modify state, it's handled by middleware
      return state;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  setCartOpen,
  setBookingType,
  updateEventDetails,
  updateServiceRequirements,
  loadCartFromStorage,
  saveCartToStorage,
} = cartSlice.actions;

export default cartSlice.reducer;
